/**
 * Flags for the region switcher (CNA-1906).
 *
 * Inline SVG rather than emoji or an icon package:
 *
 * - **Emoji do not work.** Windows ships no country-flag glyphs, so `🇨🇦` renders as the
 *   letters "CA" there — exactly what these exist to get away from.
 * - **Not `packages/assets`.** That bucket syncs to S3 *after* the ECS rollouts, so a new
 *   image there 404s for several minutes after each deploy (the same ordering gap that made
 *   stage render raw i18n keys on 22 Oct). Inline SVG ships inside the bundle, atomic with
 *   the code that uses it.
 *
 * Both are drawn on the conventional 4:3 flag viewBox (640×480) and sized by the caller.
 *
 * `aria-hidden` on both: the flag repeats what the adjacent label already says, so
 * announcing it would make a screen reader read the region twice.
 */

import type { SVGProps } from 'react';

interface FlagProps {
  /** Rendered width in px; height follows the 4:3 ratio. */
  size?: number;
}

const BASE = { display: 'block', flexShrink: 0, borderRadius: 1 } as const;

// Return type annotated rather than inferred: without it `focusable: 'false'` widens to
// `string`, which SVGProps rejects (it wants Booleanish | "auto"). Annotating also means
// any future attribute added here is checked against the real SVG prop types.
const frame = (size: number): SVGProps<SVGSVGElement> => ({
  width: size,
  height: (size * 3) / 4,
  viewBox: '0 0 640 480',
  role: 'img',
  'aria-hidden': true,
  focusable: 'false',
  style: BASE,
});

/**
 * Canada — ca-central-1.
 *
 * Leaf and bars are the real geometry from the `flag-icons` project (MIT licensed,
 * github.com/lipis/flag-icons), not an approximation — a hand-drawn eleven-point leaf reads
 * as a red smudge at this size.
 */
export const FlagCa = ({ size = 20 }: FlagProps) => (
  <svg {...frame(size)}>
    <path fill="#fff" d="M150.1 0h339.7v480H150z" />
    <path
      fill="#d52b1e"
      d="M-19.7 0h169.8v480H-19.7zm509.5 0h169.8v480H489.9zM201 232l-13.3 4.4 61.4 54c4.7 13.7-1.6 17.8-5.6 25l66.6-8.4-1.6 67 13.9-.3-3.1-66.6 66.7 8c-4.1-8.7-7.8-13.3-4-27.2l61.3-51-10.7-4c-8.8-6.8 3.8-32.6 5.6-48.9 0 0-35.7 12.3-38 5.8l-9.2-17.5-32.6 35.8c-3.5.9-5-.5-5.9-3.5l15-74.8-23.8 13.4q-3.2 1.3-5.2-2.2l-23-46-23.6 47.8q-2.8 2.5-5 .7L264 130.8l13.7 74.1c-1.1 3-3.7 3.8-6.7 2.2l-31.2-35.3c-4 6.5-6.8 17.1-12.2 19.5s-23.5-4.5-35.6-7c4.2 14.8 17 39.6 9 47.7"
    />
  </svg>
);

/**
 * A five-pointed star, point upwards, as an SVG path.
 *
 * The inner radius is not a free choice: for the points to be straight-sided the ratio is
 * sin(18°)/sin(126°) ≈ 0.382. Eyeballing it gives a star with bent edges.
 */
const star = (cx: number, cy: number, outer: number): string => {
  const inner = outer * (Math.sin(Math.PI / 10) / Math.sin((7 * Math.PI) / 10));
  const points = Array.from({ length: 10 }, (_, i) => {
    const r = i % 2 === 0 ? outer : inner;
    const angle = (i * Math.PI) / 5 - Math.PI / 2; // start at the top
    return `${(cx + r * Math.cos(angle)).toFixed(2)},${(cy + r * Math.sin(angle)).toFixed(2)}`;
  });
  return `M${points.join('L')}Z`;
};

// Official proportions: the stars sit on a circle of radius 1/3 the flag's height, each
// star circumscribed by a circle of radius 1/18 of the height. Twelve of them, evenly
// spaced, twelve o'clock first — the count is fixed and unrelated to member states.
const EU_STARS = Array.from({ length: 12 }, (_, i) => {
  const angle = (i * Math.PI) / 6 - Math.PI / 2;
  return star(320 + 160 * Math.cos(angle), 240 + 160 * Math.sin(angle), 480 / 18);
});

/** Europe — eu-central-1. */
export const FlagEu = ({ size = 20 }: FlagProps) => (
  <svg {...frame(size)}>
    <path fill="#039" d="M0 0h640v480H0z" />
    {EU_STARS.map((d) => (
      <path key={d} fill="#fc0" d={d} />
    ))}
  </svg>
);

/** Flag for a region code, or nothing if a new region ever arrives without one. */
export const RegionFlag = ({ code, size }: { code: string; size?: number }) => {
  if (code === 'ca') return <FlagCa size={size} />;
  if (code === 'eu') return <FlagEu size={size} />;
  return null;
};
